// src/components/rpa/ProjectForm.js
// UPDATED: client is now SELECTED from the RPAClient collection instead of
// free-typed. Free-typing is what produced the near-duplicate records
// ("Acorn Tech" vs "Acorn Tech, Inc.") in the first place.
//
// The form sends clientId; createProjectController resolves the name from the
// record so RPAProject.client and RPAProject.clientId can never disagree.
// "New client" is still available inline, but it goes through
// POST /admin/rpa/clients, so the record exists before the project does.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const ProjectForm = ({ onProjectCreated, onClientCreated, initialClientId = '' }) => {
  const [formData, setFormData] = useState({
    clientId: initialClientId,
    name: '',
    description: '',
    isCollaborative: false
  });

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientsError, setClientsError] = useState('');

  const [mode, setMode] = useState('select'); // 'select' | 'new'
  const [newClient, setNewClient] = useState({ name: '', contactName: '', contactEmail: '' });
  const [creatingClient, setCreatingClient] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const fetchClients = useCallback(async () => {
    try {
      setLoadingClients(true);
      setClientsError('');
      const res = await axios.get(`${API_URL}/admin/rpa/clients`, {
        params: { status: 'active', rollups: 0 },
        withCredentials: true
      });
      setClients(res.data?.clients || []);
    } catch (err) {
      setClientsError(err.response?.data?.message || 'Could not load clients');
    } finally {
      setLoadingClients(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreateClient = async () => {
    setError('');
    setNotice('');
    const name = newClient.name.trim();
    if (!name) {
      setError('Client name is required');
      return;
    }

    try {
      setCreatingClient(true);
      const res = await axios.post(
        `${API_URL}/admin/rpa/clients`,
        {
          name,
          contactName: newClient.contactName || null,
          contactEmail: newClient.contactEmail || null
        },
        { withCredentials: true }
      );

      const created = res.data?.client;
      if (created) {
        setClients((prev) =>
          [...prev.filter((c) => String(c._id) !== String(created._id)), created].sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        );
        setFormData((f) => ({ ...f, clientId: String(created._id) }));
        setNewClient({ name: '', contactName: '', contactEmail: '' });
        setMode('select');
        setNotice(`Client "${created.name}" created and selected`);
        if (onClientCreated) onClientCreated(created);
      }
    } catch (err) {
      // 409 means the name already exists — select the existing record rather
      // than making the user retype it.
      const existing = err.response?.data?.client;
      if (err.response?.status === 409 && existing) {
        setClients((prev) =>
          prev.some((c) => String(c._id) === String(existing._id)) ? prev : [...prev, existing]
        );
        setFormData((f) => ({ ...f, clientId: String(existing._id) }));
        setMode('select');
        setNotice(`"${existing.name}" already exists — selected it for you`);
      } else {
        setError(err.response?.data?.message || 'Failed to create client');
      }
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (!formData.clientId || !formData.name.trim()) {
      setError('Client and project name are required');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/admin/rpa/projects`,
        {
          clientId: formData.clientId,
          name: formData.name.trim(),
          description: formData.description,
          isCollaborative: formData.isCollaborative
        },
        { withCredentials: true }
      );

      // controller may return { ok: true } or { success: true } — check both
      if (response.data.ok || response.data.success) {
        setFormData({ clientId: formData.clientId, name: '', description: '', isCollaborative: false });
        setNotice('Project created successfully');
        if (onProjectCreated) onProjectCreated(response.data.project);
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.response?.data?.error || 'Failed to create project'
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find((c) => String(c._id) === String(formData.clientId));

  return (
    <div style={containerStyle}>
      <h3 style={titleStyle}>Create New Project</h3>

      {error && <div style={errorBoxStyle}>{error}</div>}
      {notice && <div style={noticeBoxStyle}>{notice}</div>}

      <div style={formStyle}>
        {/* ---- Client ---- */}
        <div style={fieldStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={labelStyle}>Client *</label>
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'new' ? 'select' : 'new');
                setError('');
                setNotice('');
              }}
              style={linkButtonStyle}
              disabled={loading}
            >
              {mode === 'new' ? 'Choose existing' : '+ New client'}
            </button>
          </div>

          {mode === 'select' ? (
            <>
              <select
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                style={inputStyle}
                disabled={loading || loadingClients}
              >
                <option value="">
                  {loadingClients ? 'Loading clients…' : '— Select a client —'}
                </option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {clientsError && (
                <div style={{ fontSize: 12, color: '#b91c1c' }}>
                  {clientsError}{' '}
                  <button type="button" onClick={fetchClients} style={linkButtonStyle}>
                    Retry
                  </button>
                </div>
              )}
              {!loadingClients && !clientsError && clients.length === 0 && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  No clients yet — use “+ New client” to add the first one.
                </div>
              )}
              {selectedClient?.contactName && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Contact: {selectedClient.contactName}
                  {selectedClient.contactEmail ? ` · ${selectedClient.contactEmail}` : ''}
                </div>
              )}
            </>
          ) : (
            <div style={newClientBoxStyle}>
              <input
                type="text"
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                placeholder="Client name (e.g., Acorn Technologies, Inc.)"
                style={inputStyle}
                disabled={creatingClient}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={newClient.contactName}
                  onChange={(e) => setNewClient({ ...newClient, contactName: e.target.value })}
                  placeholder="Primary contact (optional)"
                  style={{ ...inputStyle, flex: 1 }}
                  disabled={creatingClient}
                />
                <input
                  type="email"
                  value={newClient.contactEmail}
                  onChange={(e) => setNewClient({ ...newClient, contactEmail: e.target.value })}
                  placeholder="Contact email (optional)"
                  style={{ ...inputStyle, flex: 1 }}
                  disabled={creatingClient}
                />
              </div>
              <button
                type="button"
                onClick={handleCreateClient}
                disabled={creatingClient}
                style={{ ...secondaryButtonStyle, opacity: creatingClient ? 0.6 : 1 }}
              >
                {creatingClient ? 'Creating…' : 'Create client'}
              </button>
            </div>
          )}
        </div>

        {/* ---- Project name ---- */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Project Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., FY2025 Financial Statement Audit"
            style={inputStyle}
            disabled={loading}
          />
        </div>

        {/* ---- Description ---- */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Optional project description"
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            disabled={loading}
          />
        </div>

        {/* ---- Collaborative ---- */}
        <div style={checkboxFieldStyle}>
          <label style={checkboxLabelStyle}>
            <input
              type="checkbox"
              checked={formData.isCollaborative}
              onChange={(e) => setFormData({ ...formData, isCollaborative: e.target.checked })}
              disabled={loading}
              style={checkboxStyle}
            />
            <div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                Make this project collaborative
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                {formData.isCollaborative
                  ? '✓ Other admins at @tjgcpas.com can view and work on this project'
                  : '○ Only you will be able to see and access this project'}
              </div>
            </div>
          </label>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || mode === 'new'}
          style={{
            ...buttonStyle,
            opacity: loading || mode === 'new' ? 0.6 : 1,
            cursor: loading || mode === 'new' ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Creating...' : 'Create Project'}
        </button>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '24px',
  marginBottom: '24px'
};

const titleStyle = {
  margin: '0 0 16px 0',
  fontSize: '18px',
  fontWeight: '700',
  color: '#1f2937'
};

const errorBoxStyle = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  padding: '12px',
  borderRadius: '8px',
  marginBottom: '16px',
  fontSize: '14px'
};

const noticeBoxStyle = {
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  color: '#065f46',
  padding: '12px',
  borderRadius: '8px',
  marginBottom: '16px',
  fontSize: '14px'
};

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
};

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px'
};

const labelStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#374151'
};

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  background: '#fff'
};

const newClientBoxStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
  background: '#f9fafb',
  border: '1px dashed #d1d5db',
  borderRadius: '8px'
};

const linkButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#4f46e5',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
  padding: 0
};

const secondaryButtonStyle = {
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  padding: '8px 14px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  alignSelf: 'flex-start'
};

const checkboxFieldStyle = {
  padding: '16px',
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '8px'
};

const checkboxLabelStyle = {
  display: 'flex',
  gap: '12px',
  cursor: 'pointer',
  alignItems: 'flex-start'
};

const checkboxStyle = {
  width: '18px',
  height: '18px',
  cursor: 'pointer',
  marginTop: '2px'
};

const buttonStyle = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  border: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '15px',
  fontWeight: '600',
  marginTop: '8px'
};

export default ProjectForm;